import { ZuiBaseElement } from '@zywave/zui-base';
import { findAssignedElement, findAssignedElements } from '@zywave/zui-base/dist/utils/find-assigned-element.js';
import { html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { style } from './zui-expander-css.js';
import '@zywave/zui-icons';

/**
 * A standalone `<zui-expander>` renders as the default 'peek-a-boo' expander. `<zui-expander` `type="group-standard">`'s passed into a `<zui-expander-group` `type="ground-standard">` render as a 'standard expander', or `group-standard` type.
 *
 * @element zui-expander
 *
 * @slot content - (all types): slot for the content that will be visible depending on if `open` or not
 * @slot title - (`group-standard`): slot for title text, applicable to `<zui-expander>`'s in `<zui-group-expander>` only
 *
 * @event openchanged - Event is fired when component loads and if it is opened or closed, contains details regarding `open` state
 *
 * @cssprop [--zui-expander-trigger-placement=flex-end] - Flexibility in where consumers place the trigger that opens/closes expanders
 * @cssprop [--zui-expander-trigger-animation=transform 0.2s ease-out] - Customize timing and easing for animation reveals, helpful as default can feel off based on content size
 */
export class ZuiExpander extends ZuiBaseElement {
  /**
   * Determines whether an expander is open or closed
   */
  @property({ type: Boolean, reflect: true, attribute: 'open' })
  open = false;

  /**
   * For standalone expanders aka 'peek-a-boo' expanders, no type value needs to be specified; for all `<zui-expander>`'s in a `<zui-expander-group>` a `type="group-standard"` must be specified.
   * @type { 'group-standard' | null}
   */
  @property({ type: String, reflect: true, attribute: 'type' })
  type: ZuiExpanderType = null;

  /**
   * By default in standard aka peekaboo expanders, the closed state text says 'See more', assign a new value if necessary
   */
  @property({ type: String, attribute: 'expand-label' })
  expandLabel = 'See more';

  /**
   * By default in standard aka peekaboo expanders, the opened state text says 'See less', assign a new value if necessary
   */
  @property({ type: String, attribute: 'collapse-label' })
  collapseLabel = 'See less';

  @query('slot[name="trigger"]')
  _triggerSlotEl: HTMLSlotElement;

  @query('slot[name="footer-button"]')
  _footerSlotEl: HTMLSlotElement;

  private get _trigger() {
    return findAssignedElement(this._triggerSlotEl, '*');
  }

  private get _footerTriggers() {
    return findAssignedElements(this._footerSlotEl, '[expander-close]');
  }

  protected setOpen(force: boolean) {
    this.open = force;
  }

  private toggleOpen() {
    this.open = !this.open;
  }

  private onKeydownToggleOpen() {
    const clickKeys = ['Space', 'Enter'];

    this.addEventListener('keydown', (e) => {
      if (clickKeys.includes(e.code) && this === document.activeElement) {
        e.preventDefault();
        e.stopPropagation();
        this.toggleOpen();
      }
    });
  }

  private setTabIndex() {
    this.tabIndex = 0;
    this.setAttribute('role', 'button');
  }

  protected firstUpdated() {
    if (this.type === 'group-popout' && this._trigger) {
      this._trigger.addEventListener('click', () => this.toggleOpen());
    }

    if (this.type === 'group-popout' && this._footerTriggers) {
      this._footerTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          this.toggleOpen();
        });
      });
    }

    if (this.type === 'group-lightweight' || this.type === 'group-standard') {
      this.setTabIndex();
      this.onKeydownToggleOpen();
    }
  }

  static get styles() {
    const result = [super.styles, style];

    return result;
  }

  render() {
    this._raiseOpenCloseEvent(this.open);

    let expanderTemplate;
    switch (this.type) {
      case 'group-standard':
        expanderTemplate = this._renderGroupStandard();
        break;
      case 'group-lightweight':
        expanderTemplate = this._renderGroupLightweight();
        break;
      case 'group-popout':
        expanderTemplate = this._renderGroupPopout();
        break;
      default:
        expanderTemplate = this._renderStandard();
        break;
    }

    return html` ${expanderTemplate} `;
  }

  private _renderGroupStandard() {
    return html`
      <div class="trigger" @click=${this.toggleOpen}>
        <slot name="title"></slot>
        <zui-icon icon="zui-down"></zui-icon>
      </div>
      <slot name="content"></slot>
    `;
  }

  private _renderGroupLightweight() {
    return html`
      <div class="header-content-wrapper">
        <div class="trigger" @click=${this.toggleOpen}>
          <zui-icon icon="zui-right"></zui-icon>
          <slot name="title"></slot>
        </div>
      </div>
      <slot name="content"></slot>
    `;
  }

  private _renderGroupPopout() {
    return html`
      <div class="header">
        <div class="header-content-wrapper">
          <slot name="title"></slot>
          <slot name="subtitle"></slot>
        </div>
        <slot name="trigger"></slot>
      </div>
      <slot name="content"></slot>
      <div class="footer-container">
        <slot name="footer-button"></slot>
      </div>
    `;
  }

  private _renderStandard() {
    return html`
      <div class="trigger">
        <button class="clickable" @click=${this.toggleOpen}>
          <zui-icon class="small" icon="zui-down"></zui-icon>
          <span class="trigger-text-more">${this.expandLabel}</span>
          <span class="trigger-text-less">${this.collapseLabel}</span>
        </button>
      </div>
      <slot tabindex="${this.open ? '0' : '-1'}" name="content"></slot>
    `;
  }

  private _raiseOpenCloseEvent(open: boolean) {
    this.dispatchEvent(new CustomEvent('openchanged', { bubbles: true, composed: true, detail: { open } }));
  }
}

window.customElements.define('zui-expander', ZuiExpander);

export type ZuiExpanderType = 'group-standard' | 'group-popout' | 'group-lightweight' | null;

declare global {
  interface HTMLElementTagNameMap {
    'zui-expander': ZuiExpander;
  }
}
